import type {CultureAgentError, SessionResponse} from '../types';

import {cultureAgentAuthBase, cultureAgentCoreBase, cultureAgentJoinUrl} from './urls';

function headersInitToRecord(headers?: HeadersInit): Record<string, string> {
  if (headers === undefined) {
    return {};
  }
  if (headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return {...headers};
}

/**
 * Default headers for Culture Agent requests.
 * Caller headers are merged on top and override defaults when keys overlap.
 */
export function mergeCultureAgentHeaders(extra?: HeadersInit): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-Culture-Agent-Api-Key': process.env.REACT_APP_CULTURE_AGENT_API_KEY ?? '',
    ...headersInitToRecord(extra),
  };
}

/** POST /auth/anonymous-session/ (create anonymous session). */
export function fetchCultureAgentAnonymousSession(apiHost: string, headers?: HeadersInit): Promise<Response> {
  return fetch(cultureAgentJoinUrl(apiHost, `${cultureAgentAuthBase()}/anonymous-session/`), {
    method: 'POST',
    headers: mergeCultureAgentHeaders(headers),
    credentials: 'include',
  });
}

/** POST /auth/anonymous-session/refresh/ (refresh anonymous session). */
export function fetchCultureAgentRefreshSession(apiHost: string): Promise<Response> {
  return fetch(cultureAgentJoinUrl(apiHost, `${cultureAgentAuthBase()}/anonymous-session/refresh/`), {
    method: 'POST',
    headers: mergeCultureAgentHeaders(),
    credentials: 'include',
  });
}

/**
 * Create or resume an anonymous session. Throws a {@link CultureAgentError} on failure.
 */
export async function obtainCultureAgentSession(
  apiHost: string,
  sessionId?: string | null
): Promise<SessionResponse> {
  const headers: Record<string, string> = {};
  if (sessionId) {
    headers['X-Culture-Agent-Session-Id'] = sessionId;
  }
  const response = await fetchCultureAgentAnonymousSession(apiHost, headers);
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {error?: string; message?: string};
    throw {
      code: body.error ?? 'SESSION_ERROR',
      message: body.message ?? `Failed to obtain session: ${response.statusText}`,
    } satisfies CultureAgentError;
  }
  return response.json() as Promise<SessionResponse>;
}

/**
 * Refresh the anonymous session cookie. Throws a {@link CultureAgentError} on failure.
 */
export async function refreshCultureAgentSessionOrThrow(apiHost: string): Promise<void> {
  const response = await fetchCultureAgentRefreshSession(apiHost);
  if (!response.ok) {
    throw {
      code: 'REFRESH_ERROR',
      message: `Failed to refresh session: ${response.statusText}`,
    } satisfies CultureAgentError;
  }
}

/** POST /auth/anonymous-session/logout/ (logout anonymous session). */
export function fetchCultureAgentAnonymousSessionLogout(apiHost: string): Promise<Response> {
  return fetch(cultureAgentJoinUrl(apiHost, `${cultureAgentAuthBase()}/anonymous-session/logout/`), {
    method: 'POST',
    headers: mergeCultureAgentHeaders(),
    credentials: 'include',
  });
}

/** GET /core/get-data/ (e.g. chat-based method type survey result). */
export function fetchCultureAgentGetData(apiHost: string, extraHeaders?: HeadersInit): Promise<Response> {
  return fetch(cultureAgentJoinUrl(apiHost, `${cultureAgentCoreBase()}/get-data/`), {
    method: 'GET',
    headers: mergeCultureAgentHeaders(extraHeaders),
    credentials: 'include',
  });
}

/** Create a core request function and handles the session refresh on a centralized location. */
export function createCultureAgentCoreRequest(
  apiHost: string,
  refreshSession: () => Promise<void>
): (path: string, options: RequestInit, retried?: boolean) => Promise<Response> {
  const coreBase = cultureAgentCoreBase();
  const coreRequest = async (path: string, options: RequestInit, retried = false): Promise<Response> => {
    const {headers, ...rest} = options;
    const response = await fetch(cultureAgentJoinUrl(apiHost, `${coreBase}${path}`), {
      ...rest,
      headers: mergeCultureAgentHeaders(headers),
      credentials: 'include',
    });

    if (response.status === 401 && !retried) {
      await refreshSession();
      return coreRequest(path, options, true);
    }

    return response;
  };
  return coreRequest;
}
